<template>
  <button @click="show = true" class="btn btn-sm btn-primary">
    <i class="fa fa-upload"></i>Import
    <b-modal v-model="show" :title="$t('importDNC')" @close="close" no-close-on-esc no-close-on-backdrop no-enforce-focus>
      <div class="form-group">
        <label><b>{{$t('downloadTemplate')}}</b></label>
        <div>
          <button @click="downloadTemplate" class="btn btn-sm btn-secondary mr-1 mb-2">
            <i class="fa fa-fw fa-lg fa-file-excel-o"></i>callio-customer-template.xlsx
          </button>
        </div>
      </div>
      <div class="form-group">
        <label><b>{{$t('selectFile')}}</b></label>
        <b-form-file class="mb-3"
                     v-model="file"
                     :placeholder="$t('fileInputPlaceholder')"
                     :drop-placeholder="$t('fileInputPlaceholder')"
                     :browse-text="$t('selectFile')"
                     :disabled="reading||totalRows>0"
                     accept="application/x-iwork-keynote-sffnumbers,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ></b-form-file>
      </div>
      <div v-if="reading" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>
        <span>{{$t('importReadingNotice')}}</span>
      </div>
      <div v-if="importing" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>
        <span>{{$t('importingNotice')}}</span>
      </div>
      <div class="mb-3" v-if="totalRows">
        <b-progress :max="totalRows">
          <b-progress-bar :value="successCount+errorCount" :label="`${(((successCount + errorCount) / totalRows) * 100).toFixed(0)}%`"></b-progress-bar>
        </b-progress>
        <div class="text-center">{{successCount+errorCount}}/{{totalRows}} | {{$t('errors')}}: {{errorCount}}
          <a @click="downloadErrors" class="btn-link text-primary" href="javascript:;">[{{$t('detail')}}]</a>
        </div>
      </div>
      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="cancel" v-if="!reading&&importing">{{$t('cancel')}}</button>
        <button class="btn btn-primary" @click="reset" v-if="!reading&&!importing&&totalRows>0">Reset</button>
        <button v-if="totalRows===0" class="btn btn-primary" @click="process" :disabled="reading||importing">
          <b-spinner small v-if="reading||importing"></b-spinner>
          Import
        </button>
      </template>
    </b-modal>
  </button>
</template>

<script>
import fileReaderStream from 'filereader-stream';
import {saveAs} from 'file-saver'
import Excel from 'exceljs';

export default {
  data() {
    return {
      show: false,
      file: null,
      reading: false,
      importing: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      importErrors: [],
      cancelRequest: false,
    }
  },
  methods: {
    close(evt) {
      if (this.reading || this.importing) {
        evt.preventDefault();
      } else {
        this.reset();
      }
    },
    reset() {
      this.totalRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.importErrors = [];
      this.cancelRequest = false;
      this.reading = false;
      this.importing = false;
      this.file = null;
    },
    process(evt) {
      evt.preventDefault();
      if (!this.file) {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('importFileRequired'),
          icon: 'error',
        });
        return;
      }
      if (this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx')) {
        this.reading = true;
        let readStream = fileReaderStream(this.file);
        const workbook = new Excel.Workbook();
        workbook.xlsx.read(readStream).then(async () => {
          if (workbook.worksheets.length === 0) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let sheet = workbook.worksheets[0];
          if (sheet.actualRowCount < 2) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          if (!sheet.getRow(1).values.find(v => v && v.trim() === 'phone')) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let headerRow = sheet.getRow(1);
          let i = 3;
          this.totalRows = sheet.actualRowCount - 2;
          this.reading = false;
          this.importing = true;
          while (i <= sheet.actualRowCount) {
            if (this.cancelRequest) {
              break;
            }
            let item = this.getItem(headerRow, sheet.getRow(i));
            try {
              await this.$bgService.post('/dnc', item);
              this.successCount++;
            } catch (err) {
              this.importErrors.push({row: i, err: err});
              this.errorCount++;
            }
            i++;
          }
        }).catch(err => {
          this.$swal({
            title: this.$t('error'),
            text: err.message,
            icon: 'error',
          });
        }).finally(() => {
          this.reading = false;
          this.importing = false;
          this.cancelRequest = false;
          this.$bus.$emit('reloadDNCList');
        });
      } else {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('invalidFileType'),
          icon: 'error',
        });
      }
    },
    cancel() {
      this.cancelRequest = true;
    },
    downloadErrors() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Import errors');
      sheet.columns = [
        {header: this.$t('rowNumber'), key: 'row', width: 15},
        {header: this.$t('errors'), key: 'error', width: 50},
      ];
      for (let err of this.importErrors) {
        sheet.addRow({
          row: err.row,
          error: (err.err.response && err.err.response.status === 422) ? err.err.response.data.map(i => `${i.param}: ${i.msg}`).join(', ') : err.err.message
        });
      }
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'dnc.xlsx'
        )
      });
    },
    downloadTemplate: function () {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('DNC');
      this.setupSheet(sheet);
      let row = {
        phone: this.$t("phone"),
        desc: this.$t("desc"),
      };
      sheet.addRow(row);
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'dnc.xlsx'
        )
      });
    },
    setupSheet(sheet) {
      sheet.columns = [
        {header: 'phone', key: 'phone', width: 15},
        {header: 'desc', key: 'desc', width: 15},
      ];
    },
    getItem(headerRow, row) {
      let item = {active: true};
      for (let j = 1; j <= headerRow.cellCount; j++) {
        let key = headerRow.getCell(j).value;
        let value;
        if (row.getCell(j).isHyperlink) {
          value = row.getCell(j).value.text;
        } else {
          value = row.getCell(j).value;
        }
        if (value) {
          if (typeof value === 'string') {
            value = value.trim();
          }
          item[key] = value;
        }
      }
      return item;
    }
  },
  i18n: {
    messages: {
      vi: {
        importDNC: 'Tải lên danh sách DNC',
        downloadTemplate: 'Tải xuống file mẫu',
        selectFile: 'Chọn file',
        fileInputPlaceholder: 'Chọn hoặc kéo thả file',
        importReadingNotice: 'Đang đọc dữ liệu...',
        importingNotice: 'Dữ liệu đang được tải lên...',
        errors: 'Lỗi',
        detail: 'Chi tiết',
        importFileRequired: 'Không được bỏ trống file',
        invalidFileTemplate: 'File sai định dạng',
        invalidFileType: 'Sai loại file'
      },
      en: {
        importDNC: 'Import DNC list',
        downloadTemplate: 'Download template',
        selectFile: 'Select file',
        fileInputPlaceholder: 'Choose or drag file',
        importReadingNotice: 'Reading data...',
        importingNotice: 'Uploading data...',
        errors: 'Errors',
        detail: 'Detail',
        importFileRequired: 'File required!',
        invalidFileTemplate: 'Invalid file template',
        invalidFileType: 'Invalid file type'
      }
    }
  }
}
</script>
